import { CoreModule } from './@core/core.module';
import { JwtInterceptor } from './@core/interceptors/jwt.interceptor';
import { TimeCardRoutingModule } from './time-card/time-card-routing.module';
import { SharedModule } from './helper/shared/shared.module';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SafePipe } from '../app/helper/safe.pipe';
import { NgWizardModule, NgWizardConfig, THEME } from 'ng-wizard';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {ToastModule} from 'primeng/toast';


import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { SidebarModule } from '@syncfusion/ej2-angular-navigations';
import { MenuModule, ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { ScheduleAllModule } from '@syncfusion/ej2-angular-schedule';
import { SchedulerComponent } from './scheduler/scheduler.component';
import {
  ButtonModule,
  SwitchModule,
  CheckBoxModule,
} from '@syncfusion/ej2-angular-buttons';
import { DropDownButtonModule } from '@syncfusion/ej2-angular-splitbuttons';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { WizardComponent } from './wizard/wizard.component';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { AccordionModule } from '@syncfusion/ej2-angular-navigations';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import {
  DropDownListModule,
  MultiSelectModule,
} from '@syncfusion/ej2-angular-dropdowns';
import { NgxSpinnerModule } from 'ngx-spinner';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { UserService } from './services/user.service';
import { environment } from 'src/environments/environment';
import { JobDetailComponent } from './job-detail/job-detail.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { DatePipe } from '@angular/common';
import { ProfileComponent } from './profile/profile.component';
import { GridModule, GridAllModule  } from '@syncfusion/ej2-angular-grids';
import { TimeCardModule } from './time-card/time-card.module';
import { BsModalService } from 'ngx-bootstrap/modal';
import { MessageService } from 'primeng/api';
import { ShellModule } from './@shell/shell.module';
import { SharedComponentsModule } from './@shared/components/shared-components.module';

const ngWizardConfig: NgWizardConfig = {
  theme: THEME.default,
};

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;
/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info,
 * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: 'https://login.microsoftonline.com/' + environment.tenantId,
      redirectUri: environment.ClientUrl,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
  });
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/
 * blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.graphAPI.resourceUri,
    environment.graphAPI.resourceScopes
  );
  protectedResourceMap.set(
    environment.apiEndPoint,
    environment.JobScheduleAPI.resourceScopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: InteractionType.Redirect };
}

@NgModule({
  declarations: [
    AppComponent,
    SchedulerComponent,
    WizardComponent,
    JobDetailComponent,
    UnauthorizedComponent,
    SafePipe,
    ProfileComponent
  ],
  imports: [
    ShellModule,
    NgWizardModule.forRoot(ngWizardConfig),
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    SidebarModule,
    MenuModule,
    ScheduleAllModule,
    HttpClientModule,
    MsalModule,
    ToolbarModule,
    ButtonModule,
    DropDownButtonModule,
    CheckBoxModule,
    SwitchModule,
    DialogModule,
    DatePickerModule,
    DropDownListModule,
    AccordionModule,
    GridModule,
    GridAllModule,
    TextBoxModule,
    MultiSelectModule,
    NgxSpinnerModule,
    BrowserAnimationsModule,
    SharedModule,
    TimeCardRoutingModule,
    ToastModule,
    SharedComponentsModule,
    CoreModule
  ],
  exports: [],
  providers: [
    MessageService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    UserService,
    DatePipe,
    { provide: BsModalService },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
